import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import { TaxView } from '../../Tax';
import type { TaxViewInitial } from '../../Tax';
import TaxLedgers from '../../Bookkeeping/TaxLedgers';
import type { AwCtx } from '../ctx';
import { btn, card, errBox, note, openBlob, sectionHead } from '../ui';

// ── Tax panes (owner 2026-10-13) ────────────────────────────────────────────────────────────────
// VAT / WHT / ภ.พ.30 / ภ.ง.ด. are the Tax module's own per-client view, opened on the filing the
// submenu names (initialView) and with the cross-client picker switched off (noPicker). The shell only
// mounts these when me.modules holds 'tax' — the API behind them is gated by that key. Tax reports is
// the statutory-books side (TaxLedgers, accounting_books) and needs the shell's period.
// ⛔ The form codes below are statutory names and print identically in every locale.

const Toggle = ({ value, options, onChange }: { value: string; options: { v: TaxViewInitial; label: string }[]; onChange: (v: TaxViewInitial) => void }) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
    {options.map(o => (
      <button key={o.v} onClick={() => onChange(o.v)} style={{ ...btn, padding: '5px 11px', fontSize: 12, ...(value === o.v ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>{o.label}</button>
    ))}
  </div>
);

const frame: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden', display: 'flex', flexDirection: 'column' };

export function Pp30Pane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ ...note, marginBottom: 10 }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_pp30_caption')}</div>
      <div style={frame}><TaxView clientId={ctx.clientId} initialView="vat" noPicker /></div>
    </div>
  );
}

export function PndPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [v, setV] = useState<TaxViewInitial>('pnd1');
  return (
    <div>
      {/* ⛔ Social security belongs here. TaxView has supported 'sso' all along (TaxViewInitial),
          but this switcher offered only the three ภ.ง.ด. returns, so the one monthly filing that is
          not a tax return had no way in from the workspace — staff had to leave for the Tax module
          to reach the same client's สปส.1-10. */}
      <Toggle value={v} onChange={setV} options={[{ v: 'pnd1', label: 'ภ.ง.ด.1' }, { v: 'pnd3', label: 'ภ.ง.ด.3' }, { v: 'pnd53', label: 'ภ.ง.ด.53' }, { v: 'sso', label: 'สปส.1-10' }]} />
      {/* The removed WHT submenu's caption. ภ.ง.ด.3 and ภ.ง.ด.53 ARE the withholding returns, so the
          one piece of guidance that pane carried belongs here, where they are now shown. */}
      <div style={{ ...note, marginBottom: 10 }}>{t('aw_wht_note')}</div>
      {/* key: a new view mounts a fresh TaxView so its internal filing state is re-seeded. */}
      <div style={frame}><TaxView key={v} clientId={ctx.clientId} initialView={v} noPicker /></div>
    </div>
  );
}

export function TaxReportsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const now = new Date();
  const open = async (k: string, section: 'output' | 'input') => {
    if (busy) return; setBusy(k); setErr('');
    try { openBlob(await api.axDocUrl('vat/report', { clientId: ctx.clientId, periodId: ctx.periodId, section, year: String(now.getFullYear()), month: String(now.getMonth() + 1) })); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TaxLedgers clientId={ctx.clientId} periodId={ctx.periodId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
      <div>
        <div style={sectionHead}>{t('aw_vat_reports')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
          {([['vatout', 'รายงานภาษีขาย', 'pa_sb_c_vatout', 'output'], ['vatin', 'รายงานภาษีซื้อ', 'pa_sb_c_vatin', 'input']] as const).map(([k, th, enKey, section]) => (
            <button key={k} onClick={() => open(k, section)} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', width: '100%', opacity: busy === k ? 0.6 : 1 }}>
              <i className={busy === k ? 'ti ti-loader' : 'ti ti-file-text'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{th}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t(enKey)}</span>
              </span>
              <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
            </button>
          ))}
        </div>
        {err && <div style={{ ...errBox, marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  );
}
